/**
 * Party invites.
 *
 * `useInvites` is the shared read - the rail badge and the panel both want the
 * same list, and TanStack dedupes it to one request. Accepting is just joining
 * the party the invite points at, so there is no separate accept endpoint.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, endpoints, socket } from '@/lib/api';
import type { Party, PublicUser } from '@/lib/types';
import { go } from '@/lib/hash';
import { cn } from '@/lib/cn';
import { play } from '@/lib/sound';
import { useSession } from '@/stores/session';
import { Avatar } from './Avatar';
import { InviteLinks } from './InviteLinks';
import { UserName } from './user';
import { Button, Empty, Input, Modal, useToast } from './ui';
import * as Icons from '@/icons';

export type Invite = {
  id: string;
  partyId: string;
  createdAt: number;
  party: Party;
  from: PublicUser;
};

export function useInvites() {
  const me = useSession((s) => s.me);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['invites'],
    queryFn: () => api.get<{ invites: Invite[] }>('/api/invites'),
    enabled: !!me,
    staleTime: 30_000
  });

  // A new invite is pushed, not polled - the badge should move the moment
  // somebody presses Invite.
  useEffect(() => {
    if (!me) return;
    const off = socket.on('invite:new', () => {
      play('invite');
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
    });
    return off;
  }, [me, queryClient]);

  return query.data?.invites ?? [];
}

/** Join or decline, the same way wherever invites are shown. */
function useInviteActions(after?: () => void) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const settle = () => {
    void queryClient.invalidateQueries({ queryKey: ['invites'] });
    void queryClient.invalidateQueries({ queryKey: ['parties'] });
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };
  async function accept(invite: Invite) {
    setBusy(invite.id);
    try {
      await endpoints.joinParty(invite.partyId);
      settle();
      after?.();
      go('party/' + invite.partyId);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(null);
    }
  }
  async function decline(invite: Invite) {
    setBusy(invite.id);
    try {
      await api.post('/api/invites/' + invite.id + '/decline');
      settle();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(null);
    }
  }
  return { busy, accept, decline };
}

/**
 * Invites, right on the Parties page.
 *
 * They used to live only behind a notification - you were told somebody had
 * invited you, then could not find the invite anywhere you would look for a
 * party. Now they sit at the top of the list until you join or decline.
 */
export function InvitesStrip() {
  const invites = useInvites();
  const { busy, accept, decline } = useInviteActions();
  if (!invites.length) return null;
  return (
    <section aria-label="Party invites" className="flex flex-none flex-col gap-2">
      <h2 className="m-0 flex items-center gap-2 text-[14px] font-bold text-ink">
        <span className="h-2 w-2 rounded-full bg-accent" />
        Invites for you
        <span className="rounded-full bg-accent-soft px-2 text-[12px] font-bold text-accent">{invites.length}</span>
      </h2>
      <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(320px,1fr))]">
        {invites.map((invite) => (
          <div key={invite.id} className="flex items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-1 p-3 shadow-[inset_0_0_0_1px_var(--accent-line)]">
            <Avatar user={invite.from} size={40} />
            <div className="min-w-0 flex-1">
              <p className="m-0 flex min-w-0 items-center gap-1.5 text-[12.5px] text-mute">
                <UserName user={invite.from} className="truncate font-semibold text-ink" />
                <span className="flex-none">invited you</span>
              </p>
              <p className="m-0 truncate text-[14.5px] font-bold text-ink">{invite.party.title}</p>
              <p className="m-0 text-[12px] text-mute">{invite.party.memberCount}/{invite.party.size} players</p>
            </div>
            <Button size="sm" kind="primary" disabled={busy === invite.id} onClick={() => void accept(invite)}>Join</Button>
            <Button size="sm" kind="ghost" disabled={busy === invite.id} onClick={() => void decline(invite)}>Decline</Button>
          </div>
        ))}
      </div>
    </section>
  );
}

/* --------------------------------------------------- the invites panel */

export function InvitesPanel({ onClose }: { onClose: () => void }) {
  const invites = useInvites();
  const { busy, accept, decline } = useInviteActions(onClose);

  return (
    <Modal title="Invites" subtitle="Parties people have asked you to join" onClose={onClose}>
      {invites.length === 0 ? (
        <Empty title="No invites" note="When someone invites you to a party it lands here." />
      ) : (
        <div className="stagger flex flex-col gap-2">
          {invites.map((invite) => (
            <div
              key={invite.id}
              className="flex flex-wrap items-center gap-3 rounded-[var(--radius-s)] border border-line-soft bg-surface-1 p-3"
            >
              <Avatar user={invite.from} size={36} />
              <div className="min-w-0 flex-1">
                <p className="flex min-w-0 items-center gap-1.5 text-[13px]">
                  <UserName user={invite.from} className="font-semibold" />
                  <span className="text-mute">invited you to</span>
                </p>
                <p className="truncate text-[13.5px] font-bold">{invite.party.title}</p>
                <p className="text-[12px] text-mute">
                  {invite.party.memberCount}/{invite.party.size} · {invite.party.region}
                </p>
              </div>
              <div className="flex flex-none gap-2">
                <Button size="sm" kind="primary" disabled={busy === invite.id} onClick={() => void accept(invite)}>
                  Join
                </Button>
                <Button size="sm" kind="ghost" disabled={busy === invite.id} onClick={() => void decline(invite)}>
                  Decline
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* --------------------------------------------------- sending an invite */

export function InvitePeople({ partyId, onClose, canManage }: {
  partyId: string;
  onClose: () => void;
  /** Whether this person may make or revoke links. */
  canManage?: boolean;
}) {
  const [query, setQuery] = useState('');
  const toast = useToast();
  const [sent, setSent] = useState<string[]>([]);

  const friends = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends });

  const invite = useMutation({
    mutationFn: (username: string) => api.post('/api/parties/' + partyId + '/invite', { username }),
    onSuccess: (_data, username) => {
      setSent((list) => [...list, username]);
      toast('Invite sent', 'good');
    },
    onError: (err: Error) => toast(err.message, 'bad')
  });

  const q = query.trim().toLowerCase();
  /* Online friends only can be invited - an invite to somebody who is not
     there sits unseen and expires. Offline friends are listed after, greyed,
     so nobody wonders where they went. */
  const isOnline = (u: { status: string }) => u.status !== 'offline' && u.status !== 'invisible';
  const list = (friends.data?.friends ?? [])
    .map((f) => f.user)
    .filter((u) => !q || (u.username + ' ' + u.displayName).toLowerCase().includes(q))
    .sort((a, b) => Number(isOnline(b)) - Number(isOnline(a)));

  return (
    <Modal
      title="Invite to the party"
      subtitle="Friends first - most people only accept invites from friends."
      onClose={onClose}
      footer={<Button kind="ghost" onClick={onClose}>Done</Button>}
    >
      <div className="flex flex-col gap-[var(--gap)]">
        {/* Not everybody you want has an account you know the name of. */}
        <InviteLinks partyId={partyId} canManage={canManage !== false} />

        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search friends, or type any username"
          spellCheck={false}
        />

        {q && !list.length ? (
          <div className="flex items-center gap-3 rounded-[var(--radius-s)] border border-line-soft bg-surface-1 p-3">
            <span className="min-w-0 flex-1 truncate text-[13px]">
              Invite <strong>@{query.trim()}</strong>
            </span>
            <Button
              size="sm"
              kind="outline"
              disabled={invite.isPending || sent.includes(query.trim())}
              onClick={() => invite.mutate(query.trim())}
            >
              {sent.includes(query.trim()) ? 'Sent' : 'Invite'}
            </Button>
          </div>
        ) : null}

        <div className="flex max-h-[320px] flex-col gap-1 overflow-y-auto">
          {list.map((user) => {
            const online = isOnline(user);
            const done = sent.includes(user.username);
            return (
              <div key={user.id} className={cn('flex items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] px-2.5 py-2 transition-colors', online ? 'hover:bg-surface-2' : 'opacity-60')}>
                <Avatar user={user} size={38} presence />
                <span className="min-w-0 flex-1">
                  <UserName user={user} className="block truncate text-[15px] font-semibold" />
                  <span className="block text-[13px] text-mute">{online ? 'Online' : 'Offline - invite when they are back'}</span>
                </span>
                {online ? (
                  <Button
                    size="sm"
                    kind={done ? 'ghost' : 'primary'}
                    disabled={invite.isPending || done}
                    onClick={() => invite.mutate(user.username)}
                    icon={done ? <Icons.CheckCircle size={14} /> : undefined}
                  >
                    {done ? 'Sent' : 'Invite'}
                  </Button>
                ) : null}
              </div>
            );
          })}
          {!list.length && !q ? (
            <p className="px-3 py-6 text-center text-[12.5px] text-mute">
              Add a friend and they will show up here.
            </p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
